// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * SubmissionTracker - Shows "X/Y submitted" progress during submission collection phases
 *
 * Displays which players have submitted and which are still pending,
 * with optional player chips and animated progress.
 */

import { useMemo } from 'react';

interface PlayerSubmissionStatus {
  playerId: string;
  displayName: string;
  hasSubmitted: boolean;
  isConnected?: boolean;
}

interface SubmissionTrackerProps {
  /** List of all players with their submission status */
  players: PlayerSubmissionStatus[];
  /** Optional label for what's being submitted */
  submissionLabel?: string;
  /** Show individual player chips */
  showPlayerChips?: boolean;
  /** Compact mode for smaller spaces */
  compact?: boolean;
  /** Custom class names */
  className?: string;
}

export function SubmissionTracker({
  players,
  submissionLabel = 'submitted',
  showPlayerChips = true,
  compact = false,
  className = '',
}: SubmissionTrackerProps) {
  const { submittedCount, totalCount, progress } = useMemo(() => {
    const submitted = players.filter(p => p.hasSubmitted).length;
    const total = players.length;
    return {
      submittedCount: submitted,
      totalCount: total,
      progress: total > 0 ? (submitted / total) * 100 : 0,
    };
  }, [players]);

  const isComplete = submittedCount === totalCount;

  return (
    <div className={`submission-tracker ${className}`}>
      {/* Progress text */}
      <div
        className={`flex items-center justify-center gap-2 ${compact ? 'text-sm' : 'text-base'}`}
      >
        <span
          className={`font-semibold ${isComplete ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}
        >
          {submittedCount}/{totalCount}
        </span>
        <span className="text-gray-500 dark:text-gray-400">{submissionLabel}</span>
        {isComplete && (
          <span className="text-green-600 dark:text-green-400" aria-label="Complete">
            ✓
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div
        className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${compact ? 'h-1.5 mt-1' : 'h-2 mt-2'}`}
        role="progressbar"
        aria-valuenow={submittedCount}
        aria-valuemin={0}
        aria-valuemax={totalCount}
        aria-label={`${submittedCount} of ${totalCount} ${submissionLabel}`}
      >
        <div
          className={`h-full transition-all duration-500 ease-out ${
            isComplete ? 'bg-green-500 dark:bg-green-400' : 'bg-blue-500 dark:bg-blue-400'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Player chips */}
      {showPlayerChips && !compact && (
        <div className="flex flex-wrap gap-2 mt-3 justify-center">
          {players.map(player => (
            <PlayerChip
              key={player.playerId}
              displayName={player.displayName}
              hasSubmitted={player.hasSubmitted}
              isConnected={player.isConnected}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PlayerChipProps {
  displayName: string;
  hasSubmitted: boolean;
  isConnected?: boolean;
}

function PlayerChip({ displayName, hasSubmitted, isConnected = true }: PlayerChipProps) {
  return (
    <div
      className={`
        inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
        transition-all duration-300
        ${
          hasSubmitted
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
        }
        ${!isConnected ? 'opacity-50' : ''}
      `}
    >
      {/* Status indicator */}
      <span
        className={`
          w-2 h-2 rounded-full
          ${hasSubmitted ? 'bg-green-500' : 'bg-gray-400 animate-pulse'}
        `}
      />
      <span className="truncate max-w-[80px]">{displayName}</span>
      {hasSubmitted && <span className="text-green-600 dark:text-green-400">✓</span>}
    </div>
  );
}

export default SubmissionTracker;
